import { useQuery } from "@tanstack/react-query";
import { CornerDownRight } from "lucide-react";

import { useDataSource } from "@/adapters/data-source";
import { useOpenRecord } from "@/routes/useOpenRecord";

/**
 * "Subtask of OPS-12 · Parent title", above a subtask's own title (#666), so
 * nobody opens a piece of a bigger task without knowing it is one. The parent
 * is a link that opens it in the same panel. A parent that has since been
 * deleted is still named, as plain text, because the subtask is still its
 * child; there is nothing to open.
 */
export function ParentTaskLine({ parentTaskId }: { parentTaskId: string }) {
  const data = useDataSource();
  const { open } = useOpenRecord();
  const parentQuery = useQuery({
    queryKey: ["tasks", "detail", parentTaskId],
    queryFn: () => data.tasks.get(parentTaskId),
  });
  const parent = parentQuery.data ?? null;

  return (
    <p className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm">
      <CornerDownRight className="size-3.5 shrink-0" aria-hidden />
      <span>Subtask of</span>
      {parent === null ? (
        parentQuery.isPending ? null : (
          <span>a task that is no longer available</span>
        )
      ) : parent.deletedAt === null ? (
        <button
          type="button"
          className="text-foreground min-h-6 text-left font-medium underline-offset-2 hover:underline"
          onClick={() => {
            open(parent.id);
          }}
        >
          {parent.reference} · {parent.title}
        </button>
      ) : (
        <span>
          {parent.reference} · {parent.title} (deleted)
        </span>
      )}
    </p>
  );
}
